/* eslint-disable no-debugger, no-console, padding-line-between-statements */
import * as fs from "fs";
import env from "./utils/env.config";

const apiCall = async (url: string, APIVersion: string = env.apiV1) => {
  const res = await fetch(`${env.baseUrl}${url}`, {
    headers: {
      version: APIVersion,
    },
  });

  return res;
};

export const waitForMs = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * `res.json()`, except that 204 means there is nothing to parse.
 *
 * `/restaurant/social-media/<id>` answers 204 with a zero-byte body for every
 * restaurant with no social-media row -- eight of twelve eg restaurants
 * sampled -- and JSON.parse("") throws "Unexpected end of JSON input".
 * getRestaurantStaticProps already catches that and carries on with no social
 * media, so nothing was broken; it just logged a stack trace per page per
 * locale. One cariboucoffeenext build produced 1,263 of them.
 *
 * A 204 body is empty by definition, so returning null is what the status
 * means rather than a guess about it. Callers see exactly what they saw
 * before: getRestaurantStaticProps turns it into `{}` via `|| {}`, and the
 * item list treats a non-array as a miss and retries. #4166
 */
const parseJson = (res: Response): Promise<any> =>
  res.status === 204 ? Promise.resolve(null) : res.json();

/** `/restaurant/<id>`, the one call whose body IS the page. */
const RESTAURANT_URL = /^\/restaurant\/[0-9]*$/;

/**
 * The body of a usable answer, or a throw.
 *
 * `fetch` does NOT reject on 4xx/5xx, and nothing here checked `res.ok`, so an
 * error body was parsed as though it were data, written into
 * backendCalls.json, and prerendered into every page of the build.
 *
 * munchix shipped exactly that on 2026-09-08: the eg fleet builds every
 * restaurant at once, `/restaurant/853` answered
 *
 *   429 {"message":"Too many requests, please try again in one hour!"}
 *
 * and munchix.prepit.app went live serving that object as `restaurantInfo` --
 * no config, no franchises, `<title>Undefined</title>`, no menu. It stayed
 * that way silently, because the build succeeded. getRestaurantStaticProps
 * deliberately leaves `restaurantInfo` un-caught so a failure fails the build;
 * it never saw a failure to catch.
 *
 * Throwing here is what makes that guarantee real, and it costs no extra
 * request: #230/#234 removed the retry budget from this path because
 * getRestaurantStaticProps runs on every page and /api/revalidate re-renders
 * ~26 routes per locale synchronously, which multiplied into minutes and timed
 * out twelve UI suites. A throw is still exactly one request.
 *
 * A 200 is not automatically usable either: `/restaurant/<id>` without an `id`
 * is the same dead storefront by another route, and the item path has thrown on
 * it since #230. The guard belongs on both paths, not just the one that
 * happened to crash.
 *
 * Callers that can live without the answer already catch it -- a missing
 * social-media row resolves to `{}` and the pixels stay off, exactly as when it
 * 404s.
 */
const usableBody = async (url: string, res: Response): Promise<any> => {
  /**
   * Status BEFORE parsing, and the body read as TEXT on the failure path.
   *
   * The first version of this parsed first, which meant an error page that is
   * not JSON never reached the throw below: `res.json()` rejected with
   *
   *   SyntaxError: Unexpected token '<', "<html>...
   *
   * and that is what the build printed -- no status, no url, nothing naming the
   * call. Observed on billysbelly in run 34480657065: twenty-one restaurants
   * building four at a time put the eg backend over, one item answered with an
   * HTML error page, and `Export encountered an error on /item/[item]` was all
   * there was to go on. The build DID fail, which is the point of the guard, it
   * just failed illegibly -- the exact thing #242 removed the 204 stack traces
   * to stop.
   *
   * Text also cannot throw, so the message survives a truncated or empty body.
   */
  if (!res.ok) {
    const text = await res.text().catch(() => "<unreadable body>");

    throw new Error(
      `[apiCache] ${url} answered HTTP ${res.status}: ${text
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 200)}`
    );
  }

  const body = await parseJson(res);

  if (RESTAURANT_URL.test(url) && !body?.id) {
    throw new Error(
      `[apiCache] ${url} answered without an id, refusing to prerender against it: ${JSON.stringify(
        body
      ).slice(0, 200)}`
    );
  }

  return body;
};

export const fetchCall = async (
  url: string,
  APIVersion: string = env.apiV1,
  itemId: number | undefined = undefined
) => {
  const filePath = "backendCalls.json";
  let result: any = "N/A";
  const exists = await fs.existsSync(filePath);
  if (exists && process.env.NODE_ENV !== "development") {
    const cachedApis = await fs.readFileSync(filePath, "utf-8");
    const obj = await JSON.parse(cachedApis as unknown as string);
    const cachedItems = obj[`${url}/allItems`];
    // A cached restaurant whose item list is missing or malformed is not a
    // usable hit -- fetch it again rather than .find on it.
    if (obj[url] && (!itemId || Array.isArray(cachedItems))) {
      if (itemId) {
        const itemData = cachedItems.find((item: any) => item.id === itemId);
        result = { restaurantData: obj[url], itemData };
      } else {
        result = obj[url];
      }
    } else {
      const res = await apiCall(url, APIVersion);
      const data = await usableBody(url, res);
      let allItems: any[] = [];
      // `data.id` is guaranteed here: usableBody refuses a restaurant body
      // without one, so there is no longer a second "cannot resolve item"
      // throw for the same condition.
      if (RESTAURANT_URL.test(url)) {
        allItems = await fetchAllItemsPerRestaurant(data.id);
        obj[`${url}/allItems`] = allItems;
      }
      obj[url] = data;
      if (itemId) {
        const itemData = allItems.find((item: any) => item.id === itemId);
        result = { restaurantData: obj[url], itemData };
      } else {
        result = obj[url];
      }
    }
    const json = JSON.stringify(obj);
    await fs.writeFileSync(filePath, json, "utf-8");
  } else {
    console.log(filePath, "NOT EXIST");
    // For lambda cache

    if (itemId) {
      const restaurantId = url.split("/")[2];
      const itemUrl = `/items/item/${itemId}?restaurantId=${restaurantId}`;
      const itemRes = await apiCall(itemUrl, env.apiV2);
      const itemData = await usableBody(itemUrl, itemRes);
      result = { itemData };
    } else {
      const res = await apiCall(url, APIVersion);
      const data = await usableBody(url, res);
      result = data;
    }
  }
  return result;
};

const ITEMS_ATTEMPTS = 3;

/**
 * The item list, which must be an array.
 *
 * A build pulls the restaurant's entire menu -- cariboucoffeenext is 11.7MB and
 * ~20s idle -- and the eg fleet builds every restaurant at once. Under that
 * burst this answered with an error body instead of the array and .find on it
 * aborted the whole build on its first item page ("TypeError: s.find is not a
 * function"), taking down 12 of 23 eg deploys in run 34209276902.
 *
 * The retry lives HERE and nowhere else on purpose. This is the only call that
 * crashed, and it is reached only while `next build` populates
 * backendCalls.json -- never from the server, which has no such file. Every
 * other call keeps its single-request behaviour, because /api/revalidate
 * re-renders ~26 routes in every locale synchronously and each of those runs
 * getRestaurantStaticProps: a retry budget on that path multiplies into
 * minutes and timed out twelve UI suites. #4166
 */
const fetchAllItemsPerRestaurant = async (restaurantId: number) => {
  const url = `/items/list?restaurantId=${restaurantId}`;
  let lastFailure = "";

  for (let attempt = 1; attempt <= ITEMS_ATTEMPTS; attempt++) {
    try {
      const res = await apiCall(url);
      const data = await parseJson(res);

      if (Array.isArray(data)) {
        return data;
      }

      lastFailure = `HTTP ${res.status}, ${JSON.stringify(data).slice(0, 200)}`;
    } catch (error: any) {
      lastFailure = error?.message ?? String(error);
    }

    console.log(
      `[apiCache] ${url} attempt ${attempt}/${ITEMS_ATTEMPTS}: ${lastFailure}`
    );

    if (attempt < ITEMS_ATTEMPTS) {
      await waitForMs(1000 * attempt);
    }
  }

  throw new Error(
    `[apiCache] ${url} did not return an array after ${ITEMS_ATTEMPTS} attempts: ${lastFailure}`
  );
};
